import React, { useEffect, useRef, useState } from 'react';
import { Modal, ModalBody, ModalHeader, Progress } from 'reactstrap';
import PropTypes from 'prop-types';
import { toast } from 'react-toastify';
import { useDispatch } from 'react-redux';

import CallexLoader from '../../../components/CallexLoader';
import { botCdrsDownloadRecording } from '../../../store/slices/tenantSlices/dailyReportSlice';

// Opens immediately on click; fetches the recording as an authenticated blob and shows the
// loader (with download progress) inside the modal until it's ready to play.
const BotCdrRecordingModal = ({ isOpen, onClose, filePath }) => {
  const dispatch = useDispatch();
  const audioRef = useRef(null);
  const reqRef = useRef(0); // only the latest request updates state / clears the loader
  const [isLoading, setIsLoading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [blobUrl, setBlobUrl] = useState(null);

  useEffect(() => {
    if (!isOpen || !filePath) {
      setIsLoading(false);
      setBlobUrl(null);
      setProgress(0);
      return undefined;
    }
    const reqId = (reqRef.current += 1);
    let objectUrl = null;
    setIsLoading(true);
    setBlobUrl(null);
    setProgress(0);
    dispatch(
      botCdrsDownloadRecording({
        filename: filePath,
        onDownloadProgress: (e) => {
          if (reqId !== reqRef.current) return;
          if (e?.total) setProgress(Math.round((e.loaded / e.total) * 100));
        }
      })
    )
      .unwrap()
      .then((blob) => {
        if (reqId !== reqRef.current) return;
        objectUrl = URL.createObjectURL(blob);
        setBlobUrl(objectUrl);
      })
      .catch((error) => {
        if (reqId !== reqRef.current) return;
        console.log('error', error);
        toast.error('Failed to load recording');
        onClose();
      })
      .finally(() => {
        if (reqId === reqRef.current) setIsLoading(false);
      });
    return () => {
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
    // eslint-disable-next-line
  }, [isOpen, filePath]);

  const handleClose = () => {
    const audio = audioRef.current;
    if (audio) {
      audio.pause();
      audio.currentTime = 0;
    }
    onClose();
  };

  const fileName = filePath ? filePath.split('/').pop() : 'Recording';

  return (
    <Modal isOpen={isOpen} toggle={handleClose} size="md" centered>
      <ModalHeader toggle={handleClose}>Recording</ModalHeader>
      <ModalBody>
        <div className="text-muted small mb-3 text-truncate" title={fileName}>
          {fileName}
        </div>
        {isLoading || !blobUrl ? (
          <div
            className="d-flex flex-column justify-content-center align-items-center"
            style={{ minHeight: 120 }}>
            <CallexLoader />
            <div className="text-muted small mt-2">
              Fetching recording…{progress > 0 ? ` ${progress}%` : ''}
            </div>
            {progress > 0 && (
              <Progress value={progress} style={{ width: '80%', height: 6 }} className="mt-2" />
            )}
          </div>
        ) : (
          <audio ref={audioRef} src={blobUrl} controls autoPlay style={{ width: '100%' }} />
        )}
      </ModalBody>
    </Modal>
  );
};

BotCdrRecordingModal.propTypes = {
  isOpen: PropTypes.bool,
  onClose: PropTypes.func,
  filePath: PropTypes.string
};

export default BotCdrRecordingModal;
